import { useCallback, useState } from "react";
import styled from "styled-components";
import LandingButton from "../../../LandingButton/LandingButton";
import {
  BottomSheetBase,
  BSCloseBtn,
  BSFooter,
  BSHeader,
  BSResetBtn,
  BSTitle,
  CloseSvg,
} from "./BottomSheetBase";
import type {
  BottomSheetData,
  NestedFilterSection,
  NestedFilterState,
} from "./FilterSort.types";

interface FilterBottomSheetProps {
  data: BottomSheetData;
  initialState: NestedFilterState;
  onApply: (state: NestedFilterState, sections: NestedFilterSection[]) => void;
  onReset: () => void;
  onClose: () => void;
}

export const FilterBottomSheet = ({
  data,
  initialState,
  onApply,
  onReset,
  onClose,
}: FilterBottomSheetProps) => {
  const sections = data.list as NestedFilterSection[];
  const [activeTab, setActiveTab] = useState(0);
  const [localState, setLocalState] = useState<NestedFilterState>(() => {
    const init: NestedFilterState = {};
    sections.forEach((sec) => {
      const existing = initialState[sec.label];
      if (existing && existing.size > 0) {
        init[sec.label] = new Set(existing);
      } else if (sec.data.defaultValue) {
        init[sec.label] = new Set([sec.data.defaultValue]);
      } else {
        init[sec.label] = new Set();
      }
    });
    return init;
  });

  const currentSection = sections[activeTab];

  const toggleItem = useCallback(
    (sectionLabel: string, key: string, type: string) => {
      setLocalState((prev) => {
        const next = { ...prev };
        const set = new Set(prev[sectionLabel] || []);

        if (type === "radio") {
          set.clear();
          set.add(key);
        } else {
          if (set.has(key)) set.delete(key);
          else set.add(key);
        }

        next[sectionLabel] = set;
        return next;
      });
    },
    []
  );

  const handleApply = () => onApply(localState, sections);

  const handleReset = () => {
    const cleared: NestedFilterState = {};
    sections.forEach((sec) => {
      if (sec.data.defaultValue) {
        cleared[sec.label] = new Set([sec.data.defaultValue]);
      } else {
        cleared[sec.label] = new Set();
      }
    });
    setLocalState(cleared);
    onReset();
  };

  return (
    <BottomSheetBase onClose={onClose}>
      <HeaderWithBorder>
        <BSCloseBtn type="button" onClick={onClose} aria-label="Close">
          <CloseSvg />
        </BSCloseBtn>
        <BSTitle>{data.heading || "Filters"}</BSTitle>
      </HeaderWithBorder>

      <Content>
        <Sidebar>
          {sections.map((sec, idx) => (
            <SidebarTab
              key={sec.label}
              type="button"
              $active={activeTab === idx}
              onClick={() => setActiveTab(idx)}
            >
              <TabLabel $active={activeTab === idx}>{sec.label}</TabLabel>
              {sec.badge ? <TabBadge src={sec.badge} alt="" /> : null}
            </SidebarTab>
          ))}
        </Sidebar>

        <Main>
          {currentSection ? (
            <SectionContent
              section={currentSection}
              selected={localState[currentSection.label] || new Set()}
              onToggle={(key) =>
                toggleItem(currentSection.label, key, currentSection.data.type)
              }
            />
          ) : null}
        </Main>
      </Content>

      <BSFooter>
        {data.cta?.left ? (
          <BSResetBtn type="button" onClick={handleReset}>
            {data.cta.left}
          </BSResetBtn>
        ) : null}
        {data.cta?.right ? (
          <LandingButton
            customTitle={data.cta.right}
            customWidth="211px"
            customHeight="44px"
            onClick={handleApply}
            shadowVariant="none"
          />
        ) : null}
      </BSFooter>
    </BottomSheetBase>
  );
};

function SectionContent({
  section,
  selected,
  onToggle,
}: {
  section: NestedFilterSection;
  selected: Set<string>;
  onToggle: (key: string) => void;
}) {
  const { data } = section;

  if (data.type === "radio") {
    return (
      <ListWrap>
        {data.list.map((item) => (
          <RadioRow
            key={item.key}
            type="button"
            onClick={() => onToggle(item.key)}
          >
            <RadioOuter $on={selected.has(item.key)}>
              {selected.has(item.key) ? <RadioInner /> : null}
            </RadioOuter>
            <ItemLabel>{item.label}</ItemLabel>
          </RadioRow>
        ))}
      </ListWrap>
    );
  }

  if (data.type === "checkbox") {
    return (
      <ListWrap>
        {data.list.map((item) => (
          <CheckRow
            key={item.key}
            type="button"
            onClick={() => onToggle(item.key)}
          >
            <Checkbox $on={selected.has(item.key)}>
              {selected.has(item.key) ? <CheckSvg /> : null}
            </Checkbox>
            <ItemLabel>{item.label}</ItemLabel>
          </CheckRow>
        ))}
      </ListWrap>
    );
  }

  if (data.type === "card") {
    return (
      <ListWrap>
        {data.list.map((item) => {
          const info = item.cardInfo;
          if (!info) return null;
          const on = selected.has(item.key);
          return (
            <CardRow
              key={item.key}
              type="button"
              $on={on}
              onClick={() => onToggle(item.key)}
            >
              <Checkbox $on={on}>{on ? <CheckSvg /> : null}</Checkbox>
              <CardText>
                <CardTitle>{info.title}</CardTitle>
                <CardSubtitle>{info.subtitle}</CardSubtitle>
              </CardText>
            </CardRow>
          );
        })}
      </ListWrap>
    );
  }

  return null;
}

function CheckSvg() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
        fill="#fff"
      />
    </svg>
  );
}

const HeaderWithBorder = styled(BSHeader)`
  border-bottom: 1px solid #d8d8d8;
`;

const Content = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const Sidebar = styled.div`
  width: 134px;
  flex-shrink: 0;
  border-right: 1px solid #d8d8d8;
  overflow-y: auto;
  background: #f7f7f7;
  padding-top: 12px;
`;

const SidebarTab = styled.button<{ $active: boolean }>`
  position: relative;
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
  width: 128px;
  height: 48px;
  padding: 16px;
  border: none;
  background: ${(p) => (p.$active ? "#fff" : "transparent")};
  cursor: pointer;
  text-align: left;
  font: inherit;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 4px;
    width: 3px;
    height: 40px;
    border-radius: 0 2px 2px 0;
    background: ${(p) => (p.$active ? "#008cff" : "transparent")};
  }
`;

const TabLabel = styled.span<{ $active: boolean }>`
  font-size: 14px;
  font-weight: ${(p) => (p.$active ? 900 : 700)};
  color: ${(p) => (p.$active ? "#008cff" : "#000")};
  line-height: 14px;
`;

const TabBadge = styled.img`
  height: 14px;
  width: auto;
  object-fit: contain;
`;

const Main = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
`;

const ListWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RadioRow = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 0;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  font: inherit;
`;

const RadioOuter = styled.span<{ $on: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid ${(p) => (p.$on ? "#008cff" : "#d8d8d8")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const RadioInner = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #008cff;
`;

const CheckRow = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 0;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  font: inherit;
`;

const Checkbox = styled.span<{ $on: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 2px solid ${(p) => (p.$on ? "#008cff" : "#d8d8d8")};
  background: ${(p) => (p.$on ? "#008cff" : "#fff")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ItemLabel = styled.span`
  font-size: 14px;
  color: #000;
  flex: 1;
`;

const CardRow = styled.button<{ $on: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 12px;
  border: 1px solid ${(p) => (p.$on ? "#008cff" : "#d8d8d8")};
  border-radius: 8px;
  background: ${(p) => (p.$on ? "#eaf5ff" : "#fff")};
  cursor: pointer;
  text-align: left;
  font: inherit;
  margin-bottom: 8px;
`;

const CardText = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #000;
  margin-bottom: 4px;
`;

const CardSubtitle = styled.div`
  font-size: 12px;
  color: #757575;
`;
